import { Paper, Space, Stack, Title, Text, Flex } from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import classes from '../../../app.module.css';
import { RefObject } from 'react';
import { calculateCategory, chartAxisDateFormatter } from '../util';
import { useTranslation } from 'react-i18next';
import { PercentageChange } from './percentageChange';
import { CircledIcon } from '../../../shared_components';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Link } from 'react-router-dom';
import { categoryParameterHistoryData } from '../stores';

export const CategorizedParameterHistory = ({
  ref,
  parameter,
  data,
  thresholds,
  infoText,
  maxWidth,
}: {
  ref?: RefObject<HTMLDivElement | null>;
  parameter: string;
  data: categoryParameterHistoryData[];
  thresholds: number[];
  infoText: string;
  maxWidth: number;
}) => {
  const { t } = useTranslation(['common', 'reports']);

  /*  the y-Axis of categorized charts does not show values, but the names of the categories.
      the chart shows horizontal lines at the thresholds between two categories. To clarify,
      that a value between two thresholds falls into a category, the category label is shown
      in the middle between the threshold lines. customTicks calculates the positions of these
      labels. */

  const customTicks = () => {
    const firstInterval = (thresholds[1] - thresholds[0]) / 2;
    const lastInterval = (thresholds[3] - thresholds[2]) / 2;
    return [
      thresholds[0] - firstInterval,
      thresholds[0] + firstInterval,
      thresholds[1] + (thresholds[2] - thresholds[1]) / 2,
      thresholds[3] - lastInterval,
      thresholds[3] + lastInterval,
    ];
  };

  const customTickLabels = (value: number) => {
    switch (value) {
      case customTicks()[0]:
        return t('reports:very_low');
      case customTicks()[1]:
        return t('reports:low');
      case customTicks()[2]:
        return t('reports:mid');
      case customTicks()[3]:
        return t('reports:high');
      case customTicks()[4]:
        return t('reports:very_high');
      default:
        return '';
    }
  };

  const customReferenceLines = () => {
    return thresholds.map((entry) => {
      return { y: entry, color: 'var(--mantine-color-otherGrey10-0)' };
    });
  };

  return (
    <Stack ref={ref} gap={24} style={{ scrollSnapAlign: 'center' }}>
      <Paper style={{ padding: 24 }}>
        <Title
          order={5}
          style={{
            width: `calc(min(100vw, ${maxWidth}px) - (2 * var(--mantine-spacing-md) + 48px)`,
          }}
        >
          {t(`reports:${parameter}`)}
        </Title>
        {thresholds.length === 4 ? (
          <>
            <Flex>
              {
                <Title style={{ paddingRight: 'var(--mantine-spacing-xs)' }}>
                  {t(
                    `reports:${calculateCategory(data[data.length - 1].series, thresholds)}`,
                  )}
                </Title>
              }
              {data.length > 1 && (
                <PercentageChange
                  currentValue={data[data.length - 1].series}
                  previousValue={data[data.length - 2].series}
                />
              )}
            </Flex>
            <Space h={'var(--mantine-spacing-md)'} />
            <AreaChart
              h={300}
              data={data}
              dataKey="date"
              withTooltip={false}
              withDots={false}
              gridProps={{ horizontal: false }}
              tickLine="x"
              series={[
                {
                  name: `series`,
                  color: 'var(--mantine-primary-color-0)',
                },
              ]}
              curveType="linear"
              strokeWidth={3}
              className={classes.areaChart}
              xAxisProps={{
                padding: { left: 24, right: 24 },
                tickFormatter: (value: Date) => chartAxisDateFormatter(value),
                tickLine: { stroke: 'var(--mantine-color-otherGrey10-0)' },
              }}
              yAxisProps={{
                id: 'yAxis',
                tickCount: 5,
                ticks: customTicks(),
                tickLine: false,
                width: 59,
                domain: [0, thresholds[4]],
                tickFormatter: (value: number) => customTickLabels(value),
                padding: { top: 20 },
              }}
              referenceLines={customReferenceLines()}
            />
          </>
        ) : (
          <Stack>
            <Space h={'var(--mantine-spacing-lg)'} />
            <CircledIcon
              icon={<WarningAmberOutlinedIcon style={{ fontSize: 48 }} />}
            />
            <Text>
              {t('reports:no_thresholds_warning')}{' '}
              <Link
                to={'/'}
                style={{
                  textDecoration: 'underline',
                }}
              >
                {t('support_team')}
              </Link>
              .
            </Text>
          </Stack>
        )}
      </Paper>
      <Paper>
        <Title order={5}>{t(`reports:${parameter}`)}</Title>
        <Space h={'var(--mantine-spacing-sm)'} />
        <Text>{infoText}</Text>
      </Paper>
    </Stack>
  );
};
